// Dynamic import to avoid bundling in main chunk (~8KB savings)
// FingerprintJS is loaded on-demand when getDeviceFingerprint() is first called
import { supabase } from '@/integrations/supabase/client';
import { logger } from './logger';

interface DeviceInfo {
  fingerprintId: string;
  browser: string;
  os: string;
  deviceName: string;
  deviceType: string;
  clientPlatform: string;
  timezone: string | null;
  language: string | null;
  screen: string | null;
  userAgent: string;
}

interface DeviceCheckResult {
  isNewDevice: boolean;
  deviceId: string | null;
  isTrusted: boolean;
  /** L'appareil a été révoqué depuis un autre appareil : la session locale doit être fermée. */
  isRevoked: boolean;
  deviceInfo: DeviceInfo;
}

/** Ligne affichée dans « Mes appareils connectés ». */
export interface UserDeviceRow {
  id: string;
  device_name: string | null;
  browser: string | null;
  os: string | null;
  device_type?: string | null;
  client_platform?: string | null;
  timezone?: string | null;
  language?: string | null;
  screen?: string | null;
  last_ip_masked?: string | null;
  last_country?: string | null;
  last_city?: string | null;
  is_trusted: boolean | null;
  first_seen_at: string | null;
  last_seen_at: string | null;
  revoked_at: string | null;
}

// Initialiser FingerprintJS une seule fois (lazy loading)
let fpPromise: Promise<any> | null = null;

/** Application native Kōken (coque Median) plutôt qu'un navigateur. */
const isNativeApp = (ua: string) => /median/i.test(ua);

/** Navigateur piloté automatiquement : outil de test ou aperçu, jamais un vrai appareil. */
const isAutomated = (ua: string) =>
  /HeadlessChrome|Playwright|Puppeteer|bot|crawler/i.test(ua) ||
  (typeof navigator !== 'undefined' && (navigator as { webdriver?: boolean }).webdriver === true);

/**
 * Détecte le navigateur à partir du user agent
 */
const detectBrowser = (userAgent: string): string => {
  if (isNativeApp(userAgent)) return 'Application Kōken';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Edg')) return 'Edge';
  if (userAgent.includes('Opera') || userAgent.includes('OPR')) return 'Opera';
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Safari')) return 'Safari';
  return 'Navigateur inconnu';
};

/**
 * Détecte l'OS à partir du user agent.
 * iPhone et iPad sont testés avant macOS : leur user agent contient « Mac OS ».
 */
const detectOS = (userAgent: string): string => {
  if (/iPhone|iPad|iPod/.test(userAgent)) return 'iOS';
  if (userAgent.includes('Android')) return 'Android';
  if (userAgent.includes('Windows')) return 'Windows';
  if (userAgent.includes('Mac OS')) return 'macOS';
  if (userAgent.includes('Linux')) return 'Linux';
  return 'Système inconnu';
};

/** Type d'appareil lisible : « iPhone », « Mac », « PC Windows »… */
const detectDeviceType = (userAgent: string): string => {
  if (userAgent.includes('iPad')) return 'iPad';
  if (/iPhone|iPod/.test(userAgent)) return 'iPhone';
  if (userAgent.includes('Android')) return /Mobile/.test(userAgent) ? 'Téléphone Android' : 'Tablette Android';
  if (userAgent.includes('Mac OS')) return 'Mac';
  if (userAgent.includes('Windows')) return 'PC Windows';
  if (userAgent.includes('Linux')) return 'Ordinateur Linux';
  return 'Appareil inconnu';
};

/** Contexte d'ouverture : application installée, navigateur, ou outil automatisé. */
const detectClientPlatform = (userAgent: string): string => {
  if (isAutomated(userAgent)) return 'Outil de test automatisé';
  if (isNativeApp(userAgent)) return 'Application installée';
  return 'Navigateur web';
};

const buildDeviceName = (deviceType: string, browser: string, platform: string): string => {
  if (platform === 'Outil de test automatisé') return `${deviceType} · test automatisé`;
  if (platform === 'Application installée') return `${deviceType} · application Kōken`;
  return `${deviceType} · ${browser}`;
};

const safeTimezone = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
};

const safeScreen = (): string | null => {
  try {
    if (typeof window === 'undefined' || !window.screen) return null;
    return `${window.screen.width}×${window.screen.height}`;
  } catch {
    return null;
  }
};

/**
 * Génère le fingerprint de l'appareil courant
 */
export const getDeviceFingerprint = async (): Promise<DeviceInfo> => {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const browser = detectBrowser(userAgent);
  const os = detectOS(userAgent);
  const deviceType = detectDeviceType(userAgent);
  const clientPlatform = detectClientPlatform(userAgent);

  const describe = (fingerprintId: string): DeviceInfo => ({
    fingerprintId,
    browser,
    os,
    deviceType,
    clientPlatform,
    deviceName: buildDeviceName(deviceType, browser, clientPlatform),
    timezone: safeTimezone(),
    language: typeof navigator !== 'undefined' ? navigator.language ?? null : null,
    screen: safeScreen(),
    userAgent,
  });

  try {
    if (!fpPromise) {
      fpPromise = import('@fingerprintjs/fingerprintjs').then((mod) => mod.default.load());
    }

    const fp = await fpPromise;
    const result = await fp.get();
    return describe(result.visitorId);
  } catch (error) {
    logger.error('[FINGERPRINT] Error generating fingerprint:', error);
    // Fallback: générer un ID basique si FingerprintJS échoue
    return describe(`fallback-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`);
  }
};

/**
 * Vérifie si l'appareil est connu et l'enregistre si nouveau.
 *
 * L'enregistrement passe par une fonction serveur afin d'y ajouter le pays et
 * l'adresse IP masquée, que le navigateur ne connait pas. En cas d'échec, on
 * retombe sur l'enregistrement direct pour ne jamais bloquer la connexion.
 */
export const checkAndRegisterDevice = async (userId: string): Promise<DeviceCheckResult> => {
  const deviceInfo = await getDeviceFingerprint();

  try {
    const { data, error } = await supabase.functions.invoke('register-device', {
      body: {
        fingerprintId: deviceInfo.fingerprintId,
        deviceName: deviceInfo.deviceName,
        browser: deviceInfo.browser,
        os: deviceInfo.os,
        deviceType: deviceInfo.deviceType,
        clientPlatform: deviceInfo.clientPlatform,
        timezone: deviceInfo.timezone,
        language: deviceInfo.language,
        screen: deviceInfo.screen,
        userAgent: deviceInfo.userAgent,
      },
    });

    if (error) throw error;

    if (data?.isRevoked) {
      logger.warn('[FINGERPRINT] Device revoked by owner, signing out');
      try {
        await supabase.auth.signOut();
      } catch (signOutError) {
        logger.warn('[FINGERPRINT] Sign out after revocation failed:', signOutError);
      }
      return {
        isNewDevice: false,
        deviceId: data.deviceId ?? null,
        isTrusted: false,
        isRevoked: true,
        deviceInfo,
      };
    }

    return {
      isNewDevice: Boolean(data?.isNewDevice),
      deviceId: data?.deviceId ?? null,
      isTrusted: Boolean(data?.isTrusted),
      isRevoked: false,
      deviceInfo,
    };
  } catch (error) {
    logger.warn('[FINGERPRINT] register-device indisponible, repli local:', error);
    return registerDeviceLocally(userId, deviceInfo);
  }
};

/** Repli sans fonction serveur : pas de pays ni d'IP, mais l'appareil reste suivi. */
const registerDeviceLocally = async (
  userId: string,
  deviceInfo: DeviceInfo
): Promise<DeviceCheckResult> => {
  try {
    const { data: existingDevice, error: selectError } = await supabase
      .from('user_devices')
      .select('id, revoked_at, is_trusted')
      .eq('user_id', userId)
      .eq('fingerprint_id', deviceInfo.fingerprintId)
      .maybeSingle();

    if (selectError) throw selectError;

    const details = {
      device_name: deviceInfo.deviceName,
      browser: deviceInfo.browser,
      os: deviceInfo.os,
      device_type: deviceInfo.deviceType,
      client_platform: deviceInfo.clientPlatform,
      timezone: deviceInfo.timezone,
      language: deviceInfo.language,
      screen: deviceInfo.screen,
      user_agent: deviceInfo.userAgent,
      last_seen_at: new Date().toISOString(),
    };

    if (existingDevice) {
      if (existingDevice.revoked_at) {
        logger.warn('[FINGERPRINT] Device revoked by owner, signing out');
        try {
          await supabase.auth.signOut();
        } catch (signOutError) {
          logger.warn('[FINGERPRINT] Sign out after revocation failed:', signOutError);
        }
        return {
          isNewDevice: false,
          deviceId: existingDevice.id,
          isTrusted: false,
          isRevoked: true,
          deviceInfo,
        };
      }

      await supabase.from('user_devices').update(details).eq('id', existingDevice.id);

      return {
        isNewDevice: false,
        deviceId: existingDevice.id,
        isTrusted: Boolean(existingDevice.is_trusted),
        isRevoked: false,
        deviceInfo,
      };
    }

    const { data: newDevice, error: insertError } = await supabase
      .from('user_devices')
      .insert({
        ...details,
        user_id: userId,
        fingerprint_id: deviceInfo.fingerprintId,
        is_trusted: false,
        first_seen_at: new Date().toISOString(),
      })
      .select('id')
      .single();

    if (insertError) throw insertError;

    return {
      isNewDevice: true,
      deviceId: newDevice?.id || null,
      isTrusted: false,
      isRevoked: false,
      deviceInfo,
    };
  } catch (error) {
    logger.error('[FINGERPRINT] Error in checkAndRegisterDevice:', error);
    // En cas d'erreur, on n'alerte pas et on ne bloque pas la connexion.
    return {
      isNewDevice: false,
      deviceId: null,
      isTrusted: false,
      isRevoked: false,
      deviceInfo,
    };
  }
};

/**
 * Marque un appareil comme de confiance
 */
export const trustDevice = async (deviceId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('user_devices')
      .update({ is_trusted: true })
      .eq('id', deviceId);

    if (error) throw error;

    logger.info('[FINGERPRINT] Device marked as trusted:', deviceId);
    return true;
  } catch (error) {
    logger.error('[FINGERPRINT] Error trusting device:', error);
    return false;
  }
};

/**
 * Supprime un appareil (révocation)
 */
export const revokeDevice = async (deviceId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('user_devices')
      .delete()
      .eq('id', deviceId);

    if (error) throw error;

    logger.info('[FINGERPRINT] Device revoked:', deviceId);
    return true;
  } catch (error) {
    logger.error('[FINGERPRINT] Error revoking device:', error);
    return false;
  }
};

/**
 * Récupère tous les appareils de l'utilisateur
 */
export const getUserDevices = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('user_devices')
      .select('id, device_name, browser, os, device_type, client_platform, is_trusted, first_seen_at, last_seen_at, revoked_at')
      .eq('user_id', userId)
      .order('last_seen_at', { ascending: false });

    if (error) throw error;

    return data || [];
  } catch (error) {
    logger.error('[FINGERPRINT] Error fetching user devices:', error);
    return [];
  }
};

/**
 * Liste des appareils du compte connecté (lecture via RPC, propre au compte).
 */
export const listMyDevices = async (): Promise<UserDeviceRow[]> => {
  const { data, error } = await supabase.rpc('get_my_devices');
  if (error) {
    logger.error('[FINGERPRINT] Error listing devices:', error);
    throw error;
  }
  return (data ?? []) as UserDeviceRow[];
};

/**
 * Révoque un appareil : sa session est fermée à sa prochaine ouverture.
 */
export const revokeMyDevice = async (deviceId: string): Promise<boolean> => {
  const { data, error } = await supabase.rpc('revoke_my_device', { p_device_id: deviceId });
  if (error) {
    logger.error('[FINGERPRINT] Error revoking device:', error);
    throw error;
  }
  return Boolean(data);
};

/**
 * Renomme un appareil pour le reconnaître plus facilement.
 */
export const renameMyDevice = async (deviceId: string, label: string): Promise<boolean> => {
  const { data, error } = await supabase.rpc('rename_my_device', {
    p_device_id: deviceId,
    p_label: label,
  });
  if (error) {
    logger.error('[FINGERPRINT] Error renaming device:', error);
    throw error;
  }
  return Boolean(data);
};
